import { tagged } from "@mirage/service-logging";
import { namespace } from "@mirage/service-operational-metrics";
import Sentry, { isSentryInitialized } from "@mirage/shared/sentry";
import { ONE_MINUTE_IN_MILLIS } from "@mirage/shared/util/constants";
import * as jobs from "@mirage/shared/util/jobs";
const logger = tagged("reportMemoryUsage");

type MemoryInfo = {
  jsHeapSizeLimit: number;
  totalJSHeapSize: number;
  usedJSHeapSize: number;
};

const ONE_MB = 1_048_576;
const MAX_MEMORY_MB = 2048; // 2GB

const JOB_NAME = "webapp/report-memory-usage";
const JOB_INTERVAL = 10 * ONE_MINUTE_IN_MILLIS;

// Exported for testing
export async function reportMemoryUsage() {
  // performance.memory is a deprecated API, so the typing is not intact, but
  // it is the only possible way to get the memory usage info now.
  const memory = (performance as { memory?: MemoryInfo }).memory;
  const { stats: memoryStats } = namespace("memory");

  if (!memory) return;

  const usedMB = Math.round(memory.usedJSHeapSize / ONE_MB);
  const totalMB = Math.round(memory.totalJSHeapSize / ONE_MB);
  const limitMB = Math.round(memory.jsHeapSizeLimit / ONE_MB);
  const usedPercent = Math.round(
    (memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100,
  );

  // Use MB to make this number much more human-readable than bytes.
  memoryStats("used_mb", usedMB);

  if (memory.jsHeapSizeLimit) {
    memoryStats("used_percent", usedPercent);
  }

  if (usedMB > MAX_MEMORY_MB) {
    if (isSentryInitialized()) {
      const error = new Error(
        `High JS memory usage exceeds ${MAX_MEMORY_MB}MB limit`,
      );

      Sentry.withScope((scope) => {
        scope.setContext("memory", {
          usedMB,
          totalMB,
          limitMB,
          usedPercent,
        });
        Sentry.captureException(error);
      });
      logger.error(error);
    }
  }
}

export function startReportMemoryUsageJob() {
  jobs.register(
    JOB_NAME,
    JOB_INTERVAL,
    /* runOnStart= */ true,
    reportMemoryUsage,
  );
}

export function cancelReportMemoryUsageJob() {
  jobs.unregister(JOB_NAME);
}
